/**
 * Lightweight cache for the user's last-known geolocation.
 *
 * Read once on app boot (silent — only when the browser already has
 * geolocation permission, no extra prompt). Used by `createService` /
 * `createEvent` so newly published entities are tagged with the publisher's
 * real coordinates → NearMe can compute haversine distance instead of a
 * deterministic pseudo-distance.
 */
const KEY = "neyra.lastGeo";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

export const getLastGeo = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(KEY) || "null");
        if (!raw) return null;
        if (Date.now() - (raw.t || 0) > MAX_AGE_MS) return null;
        if (!Number.isFinite(raw.lat) || !Number.isFinite(raw.lng)) return null;
        return { lat: raw.lat, lng: raw.lng };
    } catch { return null; }
};

export const setLastGeo = (lat, lng) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    try {
        localStorage.setItem(KEY, JSON.stringify({ lat, lng, t: Date.now() }));
    } catch { /* ignore quota */ }
};

/** Silent geolocation refresh — only fires when permission is already
 *  "granted" (Permissions API). Never prompts the user from app boot. */
export const silentRefreshGeo = async () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    try {
        if (navigator.permissions?.query) {
            const status = await navigator.permissions.query({ name: "geolocation" });
            if (status.state !== "granted") return;
        }
    } catch { /* permissions API unsupported — bail out */ return; }
    navigator.geolocation.getCurrentPosition(
        (pos) => setLastGeo(pos.coords.latitude, pos.coords.longitude),
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
};
